import React from 'react';
import * as PropTypes from 'prop-types';

import { Upload as KendoUpload } from '@progress/kendo-react-upload';
import { FieldWrapper } from '@progress/kendo-react-form';
import { Label, Error, Hint } from '@progress/kendo-react-labels';
import { Avatar } from '@progress/kendo-react-layout';

import userAvatar from '../../assets/user-avatar.jpg';

/**
 * Upload-Feld für Benutzer-Avatare
 *
 * · Ist hideUploadButton = true, wird nur der Button ausgeblendet
 *   und stattdessen eine schmale Info-Box angezeigt.
 */
export const Upload = (fieldRenderProps) => {
    const {
        valid,
        value,
        id,
        optional,
        label,
        hint,
        validationMessage,
        touched,
        userImage,
        hideUploadButton = false,
        ...others
    } = fieldRenderProps;

    const imgRef = React.useRef(null);
    const [preview, setPreview] = React.useState(userImage || userAvatar);

    const showValidationMessage = touched && validationMessage;
    const showHint = !showValidationMessage && hint;

    const hintId = showHint ? `${id}_hint` : '';
    const errorId = showValidationMessage ? `${id}_error` : '';
    const labelId = label ? `${id}_label` : '';

    const onChangeHandler = (event) => {
        fieldRenderProps.onChange({ value: event.newState });

        if (event.newState.length > 0) {
            const reader = new FileReader();
            reader.onload = (e) => setPreview(e.target.result);
            reader.readAsDataURL(event.newState[0].getRawFile());
        }
    };

    const onRemoveHandler = (event) => {
        fieldRenderProps.onChange({ value: event.newState });
        setPreview(userAvatar);
    };

    return (
        <FieldWrapper>
            <Label id={labelId} editorId={id} editorValid={valid} optional={optional}>
                {label}
                <Avatar
                    shape="circle"
                    type="image"
                    style={{ width: 100, height: 100, flexBasis: 100 }}
                >
                    <img
                        ref={imgRef}
                        src={preview}
                        alt="User Avatar"
                        style={{ width: 100, height: 100, objectFit: 'cover' }}
                    />
                </Avatar>
            </Label>

            <div className="k-form-field-wrap">
                {hideUploadButton ? (
                    /* ───────── Ersatz-Box ───────── */
                    <div
                        className="k-upload k-upload-empty"
                        style={{
                            border: '1px dashed #cbd5e0',
                            padding: '12px',
                            borderRadius: '4px',
                            textAlign: 'center',
                            color: '#4a5568',
                            fontStyle: 'italic',
                        }}
                    >
                        Das Avatarbild wird automatisch generiert.
                    </div>
                ) : (
                    /* ───────── Normale Upload-Komponente ───────── */
                    <KendoUpload
                        id={id}
                        valid={valid}
                        autoUpload={false}
                        showActionButtons={false}
                        multiple={false}
                        files={value}
                        onAdd={onChangeHandler}
                        onRemove={onRemoveHandler}
                        ariaDescribedBy={`${hintId} ${errorId}`}
                        ariaLabelledBy={labelId}
                        {...others}
                    />
                )}

                {showHint && <Hint id={hintId}>{hint}</Hint>}
                {showValidationMessage && (
                    <Error id={errorId}>{validationMessage}</Error>
                )}
            </div>
        </FieldWrapper>
    );
};

Upload.displayName = 'Upload';

Upload.propTypes = {
    valid: PropTypes.bool,
    value: PropTypes.array,
    id: PropTypes.string,
    optional: PropTypes.bool,
    label: PropTypes.string,
    hint: PropTypes.string,
    validationMessage: PropTypes.string,
    touched: PropTypes.bool,
    userImage: PropTypes.string,
    hideUploadButton: PropTypes.bool,
};
